import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { supabase } from "@/integrations/supabase/client"
import { Tables } from "@/integrations/supabase/types"
import { toast } from "sonner"
import { useAuth } from "./useAuth"

type TimesheetRecord = Tables<"timesheet_records">
type Profile = Tables<"profiles"> & {
  condominium_id: string | null
}

export type RecordType = "entrada" | "saida" | "saida_almoco" | "volta_almoco"

type TimesheetRecordWithEmployee = TimesheetRecord & {
  employee: Profile | null
}

type TimesheetReportData = {
  date: string
  entrada: string | null
  saida_almoco: string | null
  volta_almoco: string | null
  saida: string | null
  total_horas: string | null
  observacoes?: string | null
}

// Função auxiliar para converter segundos em formato de horas
const formatSecondsToHours = (seconds: number) => {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`
}

export const useTimesheetRecords = (employeeId?: string, date?: string) => {
  const { profile } = useAuth()
  
  return useQuery({
    queryKey: ["timesheet_records", employeeId, date, profile?.id],
    queryFn: async () => {
      console.log("=== DEBUG useTimesheetRecords ===")
      console.log("Profile:", profile)
      console.log("EmployeeId filter:", employeeId)
      console.log("Date filter:", date)

      if (!profile?.id) {
        console.log("Sem usuário autenticado")
        return []
      }

      let query = supabase
        .from("timesheet_records")
        .select(`
          *,
          employee:profiles!timesheet_records_employee_id_fkey(*)
        `)
        .order("record_time", { ascending: false })

      // Se é porteiro, só pode ver seus próprios registros
      if (profile.role === 'porteiro') {
        query = query.eq("employee_id", profile.id)
        console.log("Filtro porteiro aplicado - buscando registros do próprio usuário")
      }

      if (employeeId) {
        query = query.eq("employee_id", employeeId)
        console.log("Filtro por employeeId aplicado:", employeeId)
      }

      if (date) {
        query = query.eq("record_date", date)
        console.log("Filtro por data aplicado:", date)
      }

      const { data, error } = await query

      if (error) {
        console.error("Erro ao buscar registros:", error)
        throw error
      }

      console.log("Dados brutos retornados:", data)
      console.log("Quantidade de registros:", data?.length || 0)

      // Se não há dados, retornar array vazio
      if (!data || data.length === 0) {
        console.log("Nenhum registro encontrado")
        return []
      }

      // Para síndico/administração, filtrar por condomínio
      let filteredData = data
      if (profile.role === 'sindico' || profile.role === 'administracao') {
        if (profile.condominium_id) {
          filteredData = data.filter((record: any) => {
            const sameCondominium = record.employee?.condominium_id === profile.condominium_id
            console.log(`Registro ${record.id}: employee condominium = ${record.employee?.condominium_id}, user condominium = ${profile.condominium_id}, match = ${sameCondominium}`)
            return sameCondominium
          })
          console.log("Registros após filtro por condomínio:", filteredData.length)
        } else {
          console.log("Síndico/Admin sem condomínio associado")
          filteredData = []
        }
      }

      console.log("Dados finais retornados:", filteredData)
      return filteredData as TimesheetRecordWithEmployee[]
    },
    enabled: !!profile?.id
  })
}

export const useCreateTimesheetRecord = () => {
  const queryClient = useQueryClient()
  const { profile } = useAuth()

  return useMutation({
    mutationFn: async (data: { 
      record_type: RecordType; 
      location?: string; 
      photo_url?: string; 
      notes?: string;
      ai_validated?: boolean;
      face_match_score?: number;
      liveness_detected?: boolean;
    }) => {
      if (!profile?.id) throw new Error("Usuário não autenticado")

      const now = new Date()
      const recordData = {
        employee_id: profile.id,
        record_type: data.record_type,
        record_date: now.toISOString().split('T')[0],
        record_time: now.toISOString(),
        location: data.location || null,
        photo_url: data.photo_url || null,
        notes: data.notes || null,
        ai_validated: data.ai_validated || false,
        face_match_score: data.face_match_score !== undefined ? data.face_match_score : null,
        liveness_detected: data.liveness_detected || false
      }

      console.log("Criando registro de ponto:", recordData)

      const { data: record, error } = await supabase
        .from("timesheet_records")
        .insert(recordData)
        .select()
        .single()

      if (error) {
        console.error("Erro na inserção:", error)
        throw error
      }

      console.log("Registro criado com sucesso:", record)
      return record
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["timesheet_records"] })
      queryClient.invalidateQueries({ queryKey: ["timesheets"] })
      queryClient.invalidateQueries({ queryKey: ["timesheet_report"] })
      queryClient.invalidateQueries({ queryKey: ["employee_timesheet_report"] })
      toast.success("Registro de ponto criado com sucesso!")
    },
    onError: (error) => {
      console.error("Erro ao criar registro de ponto:", error)
      toast.error("Erro ao criar registro de ponto")
    }
  })
}

export const useUpdateTimesheetRecord = () => {
  const queryClient = useQueryClient()
  const { profile } = useAuth()

  return useMutation({
    mutationFn: async ({ id, ...updates }: { id: string } & Partial<TimesheetRecord>) => {
      if (!profile?.id) throw new Error("Usuário não autenticado")

      const { data, error } = await supabase
        .from("timesheet_records")
        .update(updates)
        .eq("id", id)
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["timesheet_records"] })
      queryClient.invalidateQueries({ queryKey: ["timesheets"] })
      queryClient.invalidateQueries({ queryKey: ["timesheet_report"] })
      queryClient.invalidateQueries({ queryKey: ["employee_timesheet_report"] })
      toast.success("Registro de ponto atualizado com sucesso!")
    },
    onError: (error) => {
      console.error("Erro ao atualizar registro de ponto:", error)
      toast.error("Erro ao atualizar registro de ponto")
    }
  })
}

export const useDeleteTimesheetRecord = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("timesheet_records")
        .delete()
        .eq("id", id)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["timesheet_records"] })
      queryClient.invalidateQueries({ queryKey: ["timesheets"] })
      queryClient.invalidateQueries({ queryKey: ["timesheet_report"] })
      queryClient.invalidateQueries({ queryKey: ["employee_timesheet_report"] })
      toast.success("Registro de ponto excluído com sucesso!")
    },
    onError: (error) => {
      console.error("Erro ao excluir registro de ponto:", error)
      toast.error("Erro ao excluir registro de ponto")
    }
  })
}

export const useUpdateDayTimesheet = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async ({ 
      employeeId, 
      date, 
      records, 
      existingRecords 
    }: { 
      employeeId: string
      date: string
      records: any[]
      existingRecords: any[]
    }) => {
      console.log('🔄 Atualizando registros do dia:', { employeeId, date, recordCount: records.length })
      
      // Sempre remover registros existentes daquele dia por employee/date
      console.log('🗑️ Deletando registros existentes...')
      const { error: deleteError } = await supabase
        .from("timesheet_records")
        .delete()
        .eq("employee_id", employeeId)
        .eq("record_date", date)
      
      if (deleteError) {
        console.error('❌ Erro ao deletar registros:', deleteError)
        throw deleteError
      }

      // Preparar e inserir novos registros
      if (records.length > 0) {
        const recordsToInsert = records.map((record) => ({
          employee_id: employeeId,
          record_date: date,
          record_type: record.record_type,
          record_time: record.record_time, // Já vem como ISO do modal
          location: record.location || '',
          notes: record.notes || ''
        }))

        console.log('📝 Inserindo novos registros:', recordsToInsert)
        const { error: insertError } = await supabase
          .from("timesheet_records")
          .insert(recordsToInsert)

        if (insertError) {
          console.error('❌ Erro ao inserir registros:', insertError)
          throw insertError
        }
      }

      console.log('✅ Registros atualizados com sucesso')
      return { success: true }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["timesheet_records"] })
      queryClient.invalidateQueries({ queryKey: ["employee_timesheet_report"] })
      queryClient.invalidateQueries({ queryKey: ["timesheets"] })
    },
    onError: (error) => {
      console.error("Erro ao atualizar registros do dia:", error)
      toast.error("Erro ao atualizar registros do dia")
    }
  })
}

export const useTimesheetReport = (startDate: string, endDate: string) => {
  const { profile } = useAuth()

  return useQuery({
    queryKey: ["timesheet_report", profile?.id, startDate, endDate],
    queryFn: async (): Promise<TimesheetReportData[]> => {
      if (!profile?.id) return []

      console.log("=== DEBUG useTimesheetReport ===")
      console.log("Buscando relatório de ponto para:", profile.id, startDate, endDate)

      const { data, error } = await supabase.rpc('get_timesheet_report', {
        employee_uuid: profile.id,
        start_date: startDate,
        end_date: endDate
      })

      if (error) {
        console.error("Erro ao buscar relatório:", error)
        throw error
      }
      
      console.log("Dados do relatório encontrados:", data)
      
      // Transform the data to match our expected type
      return (data || []).map((item: any) => {
        // Validate date before returning
        const dateValue = item.date
        let formattedDate = dateValue
        
        if (dateValue) {
          try {
            const date = new Date(dateValue)
            if (isNaN(date.getTime())) {
              console.warn('Invalid date in timesheet report:', dateValue)
              formattedDate = null
            } else {
              formattedDate = date.toISOString().split('T')[0] // YYYY-MM-DD format
            }
          } catch (error) {
            console.warn('Error processing date:', dateValue, error)
            formattedDate = null
          }
        }
        
        return {
          date: formattedDate,
          entrada: item.entrada,
          saida_almoco: item.saida_almoco,
          volta_almoco: item.volta_almoco,
          saida: item.saida,
          total_horas: item.total_horas && item.total_horas.includes('seconds') 
            ? formatSecondsToHours(parseInt(item.total_horas.replace(' seconds', '')))
            : item.total_horas,
          observacoes: item.observacoes
        }
      });
    },
    enabled: !!profile?.id && !!startDate && !!endDate
  })
}

export const useEmployeeTimesheetReport = (employeeId: string, startDate: string, endDate: string) => {
  const { profile } = useAuth()
  
  return useQuery({
    queryKey: ["employee_timesheet_report", employeeId, startDate, endDate],
    queryFn: async (): Promise<TimesheetReportData[]> => {
      if (!employeeId) return []

      console.log("=== DEBUG useEmployeeTimesheetReport ===")
      console.log("Buscando relatório de funcionário:", employeeId, startDate, endDate)

      // SEGURANÇA: Verificar se o usuário tem permissão (não é superadmin)
      if (profile?.role !== 'superadmin') {
        // Buscar perfil do funcionário para comparar condomínio
        const { data: employeeProfile, error: profileError } = await supabase
          .from('profiles')
          .select('condominium_id')
          .eq('id', employeeId)
          .single()
        
        if (profileError) {
          console.error("Erro ao buscar perfil do funcionário:", profileError)
          throw profileError
        }
        
        // Verificar se o funcionário pertence ao mesmo condomínio
        if (employeeProfile?.condominium_id !== profile?.condominium_id) {
          const error = new Error('Acesso negado: você não tem permissão para ver dados de funcionários de outro condomínio')
          console.error(error.message)
          throw error
        }
      }

      const { data, error } = await supabase.rpc('get_timesheet_report', {
        employee_uuid: employeeId,
        start_date: startDate,
        end_date: endDate
      })

      if (error) {
        console.error("Erro ao buscar relatório do funcionário:", error)
        throw error
      }
      
      console.log("Dados do relatório do funcionário encontrados:", data)
      
      // Transform the data to match our expected type
      return (data || []).map((item: any) => {
        // Validate date before returning
        const dateValue = item.date
        let formattedDate = dateValue
        
        if (dateValue) {
          try {
            const date = new Date(dateValue)
            if (isNaN(date.getTime())) {
              console.warn('Invalid date in employee timesheet report:', dateValue)
              formattedDate = null
            } else {
              formattedDate = date.toISOString().split('T')[0] // YYYY-MM-DD format
            }
          } catch (error) {
            console.warn('Error processing employee date:', dateValue, error)
            formattedDate = null
          }
        }
        
        return {
          date: formattedDate,
          entrada: item.entrada,
          saida_almoco: item.saida_almoco,
          volta_almoco: item.volta_almoco,
          saida: item.saida,
          total_horas: item.total_horas && item.total_horas.includes('seconds') 
            ? formatSecondsToHours(parseInt(item.total_horas.replace(' seconds', '')))
            : item.total_horas,
          observacoes: item.observacoes
        }
      });
    },
    enabled: !!employeeId && !!startDate && !!endDate
  })
}

export interface AllEmployeesTimesheetEntry {
  employee: {
    id: string
    full_name: string
    email: string | null
    cpf: string | null
  }
  records: TimesheetReportData[]
}

export const useAllEmployeesTimesheetReport = (
  employees: Array<{ id: string; full_name: string | null; email: string | null; cpf: string | null }>,
  startDate: string,
  endDate: string,
  enabled: boolean
) => {
  const employeeIds = employees.map(e => e.id).join(",")

  return useQuery({
    queryKey: ["all_employees_timesheet_report", employeeIds, startDate, endDate],
    queryFn: async (): Promise<AllEmployeesTimesheetEntry[]> => {
      if (!employees.length || !startDate || !endDate) return []

      const results = await Promise.all(
        employees.map(async (emp) => {
          const { data, error } = await supabase.rpc('get_timesheet_report', {
            employee_uuid: emp.id,
            start_date: startDate,
            end_date: endDate
          })

          if (error) {
            console.error(`Erro ao buscar relatório de ${emp.full_name}:`, error)
            return {
              employee: {
                id: emp.id,
                full_name: emp.full_name || 'Sem nome',
                email: emp.email,
                cpf: emp.cpf,
              },
              records: [] as TimesheetReportData[],
            }
          }

          const records = (data || []).map((item: any) => {
            let formattedDate = item.date
            if (item.date) {
              try {
                const d = new Date(item.date)
                formattedDate = isNaN(d.getTime()) ? null : d.toISOString().split('T')[0]
              } catch {
                formattedDate = null
              }
            }
            return {
              date: formattedDate,
              entrada: item.entrada,
              saida_almoco: item.saida_almoco,
              volta_almoco: item.volta_almoco,
              saida: item.saida,
              total_horas: item.total_horas && item.total_horas.includes('seconds')
                ? formatSecondsToHours(parseInt(item.total_horas.replace(' seconds', '')))
                : item.total_horas,
              observacoes: item.observacoes,
            } as TimesheetReportData
          })

          return {
            employee: {
              id: emp.id,
              full_name: emp.full_name || 'Sem nome',
              email: emp.email,
              cpf: emp.cpf,
            },
            records,
          }
        })
      )

      return results
    },
    enabled: enabled && employees.length > 0 && !!startDate && !!endDate,
  })
}
